import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, CheckCircle2, AlertTriangle, KeyRound, Loader2, Globe } from "lucide-react";
import { validateLicenseOnline } from "@/lib/db/license";

interface LicenseActivationProps {
  onActivated: () => void;
}

export const LicenseActivation = ({ onActivated }: LicenseActivationProps) => {
  const [licenseKey, setLicenseKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      setError("Por favor, insira uma chave de licença.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await validateLicenseOnline(licenseKey);
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onActivated();
        }, 1500);
      } else {
        setError(result.error || "Erro ao ativar licença.");
      }
    } catch (err) {
      setError("Ocorreu um erro inesperado durante a ativação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent pointer-events-none" />
      
      <Card className="w-full max-w-md bg-slate-900/80 border-indigo-500/30 backdrop-blur-xl shadow-2xl shadow-indigo-500/10 text-white overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-violet-500 via-indigo-500 to-cyan-500" />

        <CardHeader className="space-y-2 text-center pt-8">
          <div className="mx-auto w-12 h-12 bg-indigo-500/10 rounded-full flex items-center justify-center border border-indigo-500/30 mb-2">
            <KeyRound className="h-6 w-6 text-indigo-400 animate-pulse" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-100">
            Ativação do Sistema
          </CardTitle>
          <CardDescription className="text-indigo-200/60 text-xs">
            Insira sua chave de licença ItaCondo para liberar o Controle de Acessos local.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleActivate}>
          <CardContent className="space-y-4 pt-2">
            {error && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 flex items-start gap-2.5 text-rose-200 text-xs animate-shake">
                <ShieldAlert className="h-4 w-4 shrink-0 text-rose-400 mt-0.5" />
                <div>
                  <p className="font-semibold">Falha na Ativação</p>
                  <p className="opacity-90">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-start gap-2.5 text-emerald-200 text-xs">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400 mt-0.5 animate-bounce" />
                <div>
                  <p className="font-semibold">Licença Ativada!</p>
                  <p className="opacity-90">Inicializando módulos de segurança...</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="licenseKey" className="text-indigo-200/80 text-xs">
                Chave de Licença
              </Label>
              <div className="relative">
                <Input
                  id="licenseKey"
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                  disabled={loading || success}
                  className="bg-slate-950/60 border-slate-800 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 font-mono tracking-wider text-center text-sm h-11"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-[10px] text-indigo-300/40 mt-1">
              <Globe className="h-3 w-3" />
              <span>Requer conexão temporária com a internet para ativação</span>
            </div>
          </CardContent>

          <CardFooter className="pb-8 pt-4">
            <Button
              type="submit"
              disabled={loading || success}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium shadow-md shadow-indigo-600/20 active:scale-[0.98] transition-transform duration-100 h-11 text-sm gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Ativando Licença...
                </>
              ) : (
                "Ativar Sistema"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};
